/** Language and theme: remembered on the device, and on the account once signed in. */

import { useAuthMePartialUpdate } from "@agencyos/api-client";
import type { Language } from "@agencyos/i18n";
import { SegmentedControl, type ThemePreference, useThemePreference } from "@agencyos/ui-web";
import { useTranslation } from "react-i18next";

import { useSession } from "../lib/session";

function useRememberOnAccount() {
  const { accessToken } = useSession();
  const mutation = useAuthMePartialUpdate();
  return (data: { preferred_language?: Language; theme?: ThemePreference }) => {
    if (accessToken) mutation.mutate({ data });
  };
}

export function LanguageSwitcher() {
  const { t, i18n } = useTranslation();
  const remember = useRememberOnAccount();
  return (
    <SegmentedControl<Language>
      label={t("settings.language")}
      value={(i18n.resolvedLanguage ?? "fr") as Language}
      options={[
        { value: "fr", label: "FR" },
        { value: "en", label: "EN" },
      ]}
      onChange={(language) => {
        void i18n.changeLanguage(language);
        remember({ preferred_language: language });
      }}
    />
  );
}

export function ThemeSwitcher() {
  const { t } = useTranslation();
  const [preference, setPreference] = useThemePreference();
  const remember = useRememberOnAccount();
  return (
    <SegmentedControl<ThemePreference>
      label={t("settings.theme")}
      value={preference}
      options={[
        { value: "auto", label: t("settings.themeAuto") },
        { value: "light", label: t("settings.themeLight") },
        { value: "dark", label: t("settings.themeDark") },
      ]}
      onChange={(value, origin) => {
        setPreference(value, origin);
        remember({ theme: value });
      }}
    />
  );
}
